import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { AuthShell } from "@/components/auth/AuthShell";
import { useAuth } from "@/lib/auth-context";
import { captureHostedAuthReturnTo } from "@/lib/auth0";
import {
  CLIDeviceAuthError,
  approveCLIDeviceAuthorization,
  denyCLIDeviceAuthorization,
  loadCLIDeviceAuthorization,
  normalizeCLIDeviceUserCode,
  prepareCLIDeviceSignIn,
  type CLIDeviceAuthorization,
  type CLIDeviceScope,
} from "@/lib/cli-device-auth";
import { PRODUCT_NAME } from "@/lib/product";

type DeviceSearch = {
  user_code: string;
};

type DevicePageState =
  | "entry"
  | "loading"
  | "ready"
  | "submitting"
  | "approved"
  | "denied";

const SCOPE_LABELS: Record<CLIDeviceScope, string> = {
  "workspace:read": "Read workspace context",
  "account:read": "View account details",
  "credits:read": "View credit balance",
  "providers:read": "View providers",
  "offers:read": "View offers",
  "compute:read": "View compute operations",
  "compute:plan": "Plan compute",
  "compute:launch": "Create compute operations",
  "compute:terminate": "Request compute termination",
};

export const Route = createFileRoute("/device")({
  validateSearch: (search: Record<string, unknown>): DeviceSearch => ({
    user_code: typeof search.user_code === "string" ? search.user_code : "",
  }),
  head: () => ({ meta: [{ title: `Authorize CLI - ${PRODUCT_NAME}` }] }),
  component: DevicePage,
});

function safeErrorMessage(error: unknown): string {
  if (error instanceof CLIDeviceAuthError) return error.message;
  return new CLIDeviceAuthError("unavailable").message;
}

function SignInPanel({ userCode }: { userCode: string }) {
  const normalizedCode = normalizeCLIDeviceUserCode(userCode);
  return (
    <AuthShell
      title="Authorize GENRL CLI"
      subtitle="Sign in to review the access request from your terminal."
    >
      <div className="space-y-4">
        {normalizedCode ? (
          <div className="rounded-xl border hairline bg-soft px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
              User code
            </p>
            <p className="mt-1 font-mono text-lg font-semibold tracking-[0.12em]">
              {normalizedCode}
            </p>
          </div>
        ) : null}
        <a
          href="/ui/login"
          onClick={() => {
            if (typeof window === "undefined") return;
            prepareCLIDeviceSignIn(window.location, captureHostedAuthReturnTo);
          }}
          className="flex h-11 w-full items-center justify-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:opacity-95"
        >
          Sign in to continue
        </a>
      </div>
    </AuthShell>
  );
}

function CompletePanel({ status }: { status: "approved" | "denied" }) {
  const approved = status === "approved";
  return (
    <AuthShell
      title={approved ? "CLI access approved" : "CLI access denied"}
      subtitle={
        approved
          ? "Return to your terminal. GENRL CLI will finish signing in."
          : "Return to your terminal to end this login attempt or start another one."
      }
    >
      <div
        role="status"
        className="rounded-xl border hairline bg-soft px-4 py-3 text-sm text-muted-foreground"
      >
        This browser window can now be closed.
      </div>
    </AuthShell>
  );
}

export function DevicePage() {
  const { ready, user, logout } = useAuth();
  const search = Route.useSearch();
  const initialCode = normalizeCLIDeviceUserCode(search.user_code) || search.user_code;
  const [userCode, setUserCode] = useState(initialCode);
  const [authorization, setAuthorization] =
    useState<CLIDeviceAuthorization | null>(null);
  const [selectedWorkspaceID, setSelectedWorkspaceID] = useState("");
  const [pageState, setPageState] = useState<DevicePageState>("entry");
  const [errorMessage, setErrorMessage] = useState("");
  const [sessionRequired, setSessionRequired] = useState(false);
  const lookupSequence = useRef(0);
  const automaticLookupCode = useRef("");

  const loadAuthorization = async (rawCode: string) => {
    const sequence = ++lookupSequence.current;
    setPageState("loading");
    setAuthorization(null);
    setSelectedWorkspaceID("");
    setErrorMessage("");
    setSessionRequired(false);
    try {
      const details = await loadCLIDeviceAuthorization(rawCode);
      if (sequence !== lookupSequence.current) return;
      setUserCode(normalizeCLIDeviceUserCode(rawCode) || rawCode);
      setAuthorization(details);
      setPageState("ready");
    } catch (error) {
      if (sequence !== lookupSequence.current) return;
      if (error instanceof CLIDeviceAuthError && error.kind === "session_required") {
        setSessionRequired(true);
      }
      setErrorMessage(safeErrorMessage(error));
      setPageState("entry");
    }
  };

  useEffect(() => {
    const normalized = normalizeCLIDeviceUserCode(search.user_code);
    if (!ready || !user?.id || !normalized || automaticLookupCode.current === normalized) {
      return;
    }
    automaticLookupCode.current = normalized;
    void loadAuthorization(normalized);
  }, [ready, search.user_code, user?.id]);

  if (!ready) {
    return (
      <AuthShell
        title="Authorize GENRL CLI"
        subtitle="Checking your GENRL session…"
      >
        <p className="text-sm text-muted-foreground">Loading…</p>
      </AuthShell>
    );
  }

  if (!user || sessionRequired) return <SignInPanel userCode={userCode} />;
  if (pageState === "approved") return <CompletePanel status="approved" />;
  if (pageState === "denied") return <CompletePanel status="denied" />;

  const accountMismatch = Boolean(
    authorization && authorization.sessionUser.id !== user.id,
  );

  const submitDecision = async (decision: "approve" | "deny") => {
    const expectedSessionUserID = authorization?.sessionUser.id || "";
    if (
      decision === "approve" &&
      (!selectedWorkspaceID || !expectedSessionUserID || accountMismatch)
    ) {
      return;
    }
    setPageState("submitting");
    setErrorMessage("");
    setSessionRequired(false);
    try {
      const status =
        decision === "approve"
          ? await approveCLIDeviceAuthorization(
              userCode,
              selectedWorkspaceID,
              expectedSessionUserID,
            )
          : await denyCLIDeviceAuthorization(userCode);
      setPageState(status);
    } catch (error) {
      if (error instanceof CLIDeviceAuthError && error.kind === "session_required") {
        setSessionRequired(true);
      }
      setErrorMessage(safeErrorMessage(error));
      setPageState(authorization ? "ready" : "entry");
    }
  };

  const reauthenticate = async () => {
    if (typeof window !== "undefined") {
      prepareCLIDeviceSignIn(window.location, captureHostedAuthReturnTo);
    }
    setPageState("submitting");
    setErrorMessage("");
    try {
      await logout();
    } catch {
      setErrorMessage("GENRL could not sign you out. Use the account menu and try again.");
      setPageState("ready");
    }
  };

  const busy = pageState === "loading" || pageState === "submitting";

  return (
    <AuthShell
      title="Authorize GENRL CLI"
      subtitle="Confirm the client, requested access, and workspace before continuing."
    >
      <div className="space-y-5">
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            void loadAuthorization(userCode);
          }}
        >
          <label htmlFor="cli-device-user-code" className="block text-sm font-medium">
            User code
          </label>
          <div className="flex gap-2">
            <input
              id="cli-device-user-code"
              value={userCode}
              onChange={(event) => {
                lookupSequence.current += 1;
                setUserCode(event.target.value.toUpperCase());
                setAuthorization(null);
                setSelectedWorkspaceID("");
                setErrorMessage("");
                setSessionRequired(false);
                setPageState("entry");
              }}
              autoComplete="one-time-code"
              autoCapitalize="characters"
              maxLength={12}
              placeholder="ABCD-EFGH"
              className="h-11 min-w-0 flex-1 rounded-xl border hairline bg-soft px-3 font-mono tracking-[0.12em] outline-none focus:ring-2 focus:ring-primary/30"
            />
            <button
              type="submit"
              disabled={busy || !normalizeCLIDeviceUserCode(userCode)}
              className="h-11 rounded-xl border hairline bg-soft px-4 text-sm font-semibold transition hover:bg-[var(--soft-fill)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {pageState === "loading" ? "Checking…" : "Continue"}
            </button>
          </div>
        </form>

        {errorMessage ? (
          <p
            role="alert"
            className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {errorMessage}
          </p>
        ) : null}

        {authorization ? (
          <div className="space-y-4 border-t hairline pt-5">
            <div className="rounded-xl border hairline bg-soft px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
                Client
              </p>
              <p className="mt-1 font-semibold">GENRL CLI</p>
              <p className="mt-1 break-all text-xs text-muted-foreground">
                {authorization.resource}
              </p>
            </div>

            <div className="rounded-xl border hairline bg-soft px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
                Approving account
              </p>
              <p className="mt-1 font-semibold">{authorization.sessionUser.name}</p>
              <p className="mt-1 break-all text-xs text-muted-foreground">
                {authorization.sessionUser.email}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium">Requested access</p>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {authorization.scopes.map((scope) => (
                  <li key={scope} className="flex items-start justify-between gap-3">
                    <span>{SCOPE_LABELS[scope]}</span>
                    <code className="text-xs">{scope}</code>
                  </li>
                ))}
              </ul>
            </div>

            {!accountMismatch ? (
              <div className="space-y-2">
                <label htmlFor="cli-device-workspace" className="block text-sm font-medium">
                  Workspace
                </label>
                {authorization.workspaces.length > 0 ? (
                  <select
                    id="cli-device-workspace"
                    value={selectedWorkspaceID}
                    onChange={(event) => setSelectedWorkspaceID(event.target.value)}
                    className="h-11 w-full rounded-xl border hairline bg-soft px-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="">Select a workspace</option>
                    {authorization.workspaces.map((workspace) => (
                      <option key={workspace.id} value={workspace.id}>
                        {workspace.name} ({workspace.role})
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="rounded-xl border hairline bg-soft px-3 py-2 text-sm text-muted-foreground">
                    No compatible workspace can grant the requested access.
                  </p>
                )}
              </div>
            ) : null}

            <p className="text-xs text-muted-foreground">
              This request expires in about {Math.max(1, Math.ceil(authorization.expiresIn / 60))} minutes.
            </p>

            {accountMismatch ? (
              <p
                role="alert"
                className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                The browser session account does not match the account loaded in GENRL. Sign out and sign in again before approving CLI access.
              </p>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => void submitDecision("deny")}
                disabled={busy}
                className="h-11 rounded-xl border hairline bg-soft px-4 text-sm font-semibold transition hover:bg-[var(--soft-fill)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Deny
              </button>
              {accountMismatch ? (
                <button
                  type="button"
                  onClick={() => void reauthenticate()}
                  disabled={busy}
                  className="h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Sign out and choose account
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void submitDecision("approve")}
                  disabled={busy || !selectedWorkspaceID}
                  className="h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Approve
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </AuthShell>
  );
}
